package com.taocent.simple.compose.component.richtext

import androidx.compose.foundation.layout.Box
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.platform.LocalClipboard
import androidx.compose.ui.text.input.TextFieldValue
import com.taocent.simple.compose.component.richtext.core.LocalRichTextConfig
import com.taocent.simple.compose.component.richtext.core.RichTextState
import com.taocent.simple.compose.component.richtext.core.internal.toolbar.FloatingToolbar
import com.taocent.simple.compose.component.richtext.core.internal.toolbar.FloatingToolbarHostState
import com.taocent.simple.compose.component.richtext.core.internal.toolbar.LocalFloatingToolbarHost
import com.taocent.simple.compose.component.richtext.core.internal.ui.RichTextClipboardActions
import com.taocent.simple.compose.component.richtext.core.internal.ui.RichTextTextFieldCore
import kotlinx.coroutines.launch

/**
 * 普通富文本输入框 Composable。
 *
 * 文本字段行为(选区、Pointer、Android collapsed-cursor、KeyEvent、host 上报)由
 * [RichTextTextFieldCore] 提供,本 Composable 只负责:
 * - 在局部创建 [FloatingToolbarHostState] 并通过 [LocalFloatingToolbarHost] 提供;
 * - 调用 [RichTextTextFieldCore] 让文本字段把选区变化上报到这个 host;
 * - 在 host 上报 active 选区时,在文本字段同 Box 内渲染一份 [FloatingToolbar]。
 *
 * 与原 :blockrichtext.RichTextTextField 的差异:本 Composable **不**在外部 BlockState
 * 上下文中被复用,只服务于普通 `RichTextState` 场景;工具栏直接随本 Composable 生命周期。
 */
@Composable
fun RichTextTextField(
    state: RichTextState,
    modifier: Modifier = Modifier,
    placeholder: String = "请输入文本...",
    focusRequester: FocusRequester = remember { FocusRequester() },
    onUserValueChange: (() -> Unit)? = null,
    onUndo: (() -> Unit)? = null,
    onRedo: (() -> Unit)? = null,
) {
    val host = remember { FloatingToolbarHostState() }
    val clipboard = LocalClipboard.current
    val coroutineScope = rememberCoroutineScope()
    val config = LocalRichTextConfig.current

    Box(modifier = modifier) {
        CompositionLocalProvider(LocalFloatingToolbarHost provides host) {
            RichTextTextFieldCore(
                state = state,
                modifier = Modifier,
                placeholder = placeholder,
                focusRequester = focusRequester,
                // 保留旧版语义:onUserValueChange 是 () -> Unit 通知,不是消费拦截。
                onUserValueChange = { _, _ ->
                    onUserValueChange?.invoke()
                    false
                },
                onUndo = onUndo,
                onRedo = onRedo,
                // :richtext 模式:把快捷键派发给 KeyCommandRegistry,允许外部注册自定义快捷键。
                dispatchRegisteredCommands = true,
            )

            val active = host.active
            if (active != null) {
                val toolbarState = active.state
                val richTextClipboard = remember(toolbarState, clipboard) {
                    RichTextClipboardActions(clipboard, toolbarState, config)
                }
                FloatingToolbar(
                    state = toolbarState,
                    textFieldGlobalPosition = active.position,
                    textFieldHeightPx = active.heightPx,
                    anchor = active.anchor,
                    showToolbar = !active.isDragging,
                    onDismiss = { host.dismiss() },
                    onCopy = {
                        if (!toolbarState.textFieldValue.selection.collapsed) {
                            coroutineScope.launch { richTextClipboard.copySelection() }
                        }
                    },
                    onCut = {
                        if (!toolbarState.textFieldValue.selection.collapsed) {
                            coroutineScope.launch { richTextClipboard.cutSelection() }
                        }
                    },
                    onPaste = {
                        coroutineScope.launch {
                            // allowSmartPaste = true:与 Cmd/Ctrl+V 路径
                            // (RichTextKeyEventHandler.handleRichTextKeyEvent)、
                            // BlockRichTextEditor.onPaste 路径保持一致。
                            // 不开 smartPaste 时,JsonPasteParser / HtmlPasteParser / MarkdownPasteParser
                            // 都不会参与解析,纯文本 JSON 会被 PlainTextPasteParser 兜底成整段字符串插入,
                            // 而「从 ExportJsonDialog 复制出的 JSON」正是这种场景 — 必须走 smartPaste。
                            richTextClipboard.paste(allowSmartPaste = true)
                        }
                    },
                    onSelectAll = { toolbarState.selectAll() }
                )
            }
        }
    }
}
